import { useEffect, useRef, useState, type RefObject } from 'react';
import { getMainAppScrollTop, MAIN_APP_SCROLL_SELECTOR } from '../lib/mainAppScroll';

const APP_TOP_BAR_HEIGHT_PX = 56;
const TOP_BAR_HIDE_SCROLL_THRESHOLD = 64;
const TOP_BAR_SCROLL_DELTA_MIN_PX = 4;
const TOP_BAR_SHOW_NEAR_TOP_PX = 8;

const APP_TOP_BAR_VISIBLE_TOP = `calc(${APP_TOP_BAR_HEIGHT_PX}px + env(safe-area-inset-top, 0px))`;

type EmployerTaskDateStripPinState = {
  isPinned: boolean;
  /** Viewport `top` when pinned; `0px` when the main app top bar is hidden. */
  pinnedTop: string;
  spacerHeightPx: number;
};

/** Pin employer task date strip with fixed positioning (works with window or main-app scroll). */
export const useEmployerTaskDateStripPin = (
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
): EmployerTaskDateStripPinState => {
  const [isPinned, setIsPinned] = useState(false);
  const [pinnedTop, setPinnedTop] = useState('0px');
  const [spacerHeightPx, setSpacerHeightPx] = useState(0);
  const lastPanelHeightRef = useRef(0);
  const lastScrollYRef = useRef(0);
  const isTopBarHiddenRef = useRef(false);

  useEffect(() => {
    const syncTopBarHidden = (scrollY: number): void => {
      const deltaY = scrollY - lastScrollYRef.current;
      if (Math.abs(deltaY) < TOP_BAR_SCROLL_DELTA_MIN_PX) {
        return;
      }
      if (scrollY <= TOP_BAR_SHOW_NEAR_TOP_PX) {
        isTopBarHiddenRef.current = false;
      } else if (deltaY > 0 && scrollY > TOP_BAR_HIDE_SCROLL_THRESHOLD) {
        isTopBarHiddenRef.current = true;
      } else if (deltaY < 0) {
        isTopBarHiddenRef.current = false;
      }
      lastScrollYRef.current = scrollY;
    };

    const update = () => {
      const panel = panelRef.current;
      if (panel) {
        lastPanelHeightRef.current = panel.offsetHeight;
      }

      const scrollY = getMainAppScrollTop();
      syncTopBarHidden(scrollY);
      const pinnedTopValue = isTopBarHiddenRef.current ? '0px' : APP_TOP_BAR_VISIBLE_TOP;
      const anchorTop = anchorRef.current?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
      const pinThresholdPx = isTopBarHiddenRef.current ? 0 : APP_TOP_BAR_HEIGHT_PX;
      const shouldPin = anchorTop <= pinThresholdPx + 0.5;

      setSpacerHeightPx(lastPanelHeightRef.current);
      setPinnedTop(pinnedTopValue);
      setIsPinned(shouldPin);
    };

    lastScrollYRef.current = getMainAppScrollTop();
    update();
    const mainScroll = document.querySelector(MAIN_APP_SCROLL_SELECTOR);
    window.addEventListener('scroll', update, { passive: true });
    mainScroll?.addEventListener('scroll', update, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      update();
    });
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (anchor) {
      resizeObserver.observe(anchor);
    }
    if (panel) {
      resizeObserver.observe(panel);
    }

    return () => {
      window.removeEventListener('scroll', update);
      mainScroll?.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [anchorRef, panelRef]);

  return { isPinned, pinnedTop, spacerHeightPx };
};
